import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  deleteRuleSet,
  fetchRuleSet,
  fetchRuleSets,
  fetchViewTemplates,
  saveRuleSet,
} from '../api/fileUploadRules'
import { queryKeys } from '../api/queryKeys'
import type { SaveRuleSetInput } from '../types/fileUploadRules'

export function useRuleSets() {
  return useQuery({
    queryKey: queryKeys.fileUploadRules.list(),
    queryFn: fetchRuleSets,
  })
}

export function useRuleSet(tableName: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.fileUploadRules.detail(tableName || ''),
    queryFn: () => fetchRuleSet(tableName as string),
    enabled: Boolean(tableName) && enabled,
  })
}

export function useSaveRuleSetMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveRuleSetInput) => saveRuleSet(input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fileUploadRules.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.fileUploadRules.detail(input.tableName) })
    },
  })
}

export function useViewTemplates(enabled = true) {
  return useQuery({
    queryKey: queryKeys.fileUploadRules.viewTemplates(),
    queryFn: fetchViewTemplates,
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDeleteRuleSetMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tableName: string) => deleteRuleSet(tableName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fileUploadRules.all })
    },
  })
}
